import { createContext, useMemo, useCallback } from "react";
import axios from "../../utils/axios";
// settings
import actionTypes from "../../common/actionTypes";
import { DATABASE_STATUTES } from "../../common/constants";
// context
import useAuthContext from "../../hooks/useAuthContext";
// hooks
import useCustomReducer from "../../hooks/useCustomReducer";
// reducers
import { subscriptionReducer } from "../../reducers/V2/subscriptionReducer";

const SUBSCRIPTION_PLANS_ENDPOINT = "/v2/subscription-plans";

const initialState = {
  plans: [],
  status: DATABASE_STATUTES.NOT_LOADED,
  error: null,
};

const SubscriptionContext = createContext({
  ...initialState,
  fetchSubscriptionPlans: () => Promise.resolve(),
});

const SubscriptionProvider = ({ children }) => {
  const [state, dispatch] = useCustomReducer(subscriptionReducer, initialState);
  const { firebaseAuthIdToken } = useAuthContext();

  const fetchSubscriptionPlans = useCallback(
    async (version) => {
      if (!firebaseAuthIdToken) {
        return;
      }

      dispatch({ type: actionTypes.FETCH_SUBSCRIPTION_PLANS });

      try {
        const params = version ? { version } : {};

        const { data } = await axios.get(SUBSCRIPTION_PLANS_ENDPOINT, {
          headers: {
            Authorization: firebaseAuthIdToken,
          },
          params,
        });

        dispatch({
          type: actionTypes.FETCH_SUBSCRIPTION_PLANS_SUCCESS,
          payload: {
            plans: data.data || [],
          },
        });

        return data.data || [];
      } catch (error) {
        console.error("Error fetching subscription plans:", error);
        dispatch({
          type: actionTypes.FETCH_SUBSCRIPTION_PLANS_FAILURE,
          error: error,
        });
        throw error;
      }
    },
    [firebaseAuthIdToken]
  );

  const memoizedState = useMemo(
    () => ({
      ...state,
      fetchSubscriptionPlans,
    }),
    [state, fetchSubscriptionPlans]
  );

  return <SubscriptionContext.Provider value={memoizedState}>{children}</SubscriptionContext.Provider>;
};

export { SubscriptionContext, SubscriptionProvider };
